import { createSlice } from '@reduxjs/toolkit'
import { Auth } from 'aws-amplify'
import { API } from 'src/utils/interceptor'
import {
  GROWTH_ENGINE_GUIDED_LANE_REASONS,
  emitGrowthEngineGuidedLaneProgress,
} from 'src/utils/growthEngineNudgeRefresh'
import Sentry from 'src/utils/sentry'
import { applySupplierCreateBrandFlags } from 'src/utils/supplierCreateBrandTag'
import {
  SuccessNotification,
  RejectedNotification,
} from 'src/notifications/SuccessNotification'

const supplierSlice = createSlice({
  name: 'Supplier',
  initialState: {
    suppliers: [],
    paginatedSuppliers: [],
    isSuppliersLoading: false,
    omsSuppliers: [],
    supplierStats: {
      serverCount: 0,
    },
    priceFiles: [],
    priceFileMetadata: {},
    isPriceFilesLoading: false,
    isMetadataLoading: false,
  },
  reducers: {
    setSuppliers: (state, action) => {
      state.suppliers = action.payload
    },
    setPaginatedSuppliers: (state, action) => {
      state.paginatedSuppliers = action.payload
    },
    setSupplierStats: (state, action) => {
      state.supplierStats.serverCount = action.payload.count
    },
    setSupplierLoading: (state, action) => {
      state.isSuppliersLoading = action.payload
    },
    addSupplierToState: (state, action) => {
      state.suppliers.push(action.payload)
    },
    deleteSupplierFromState: (state, action) => {
      state.suppliers = state.suppliers.filter(
        item => item._id !== action.payload,
      )
    },
    editSupplierInState: (state, action) => {
      const index = state.suppliers.findIndex(
        supplier => supplier._id === action.payload._id,
      )
      if (index !== -1) state.suppliers[index] = action.payload
    },
    setSearchReindexLoading: (state, action) => {
      const supplier = state.suppliers.find(
        supplier => supplier._id === action.payload.id,
      )
      if (supplier) supplier.loadingSearchReindex = action.payload.loading
    },
    setSearchReindexResponse: (state, action) => {
      const supplier = state.suppliers.find(
        supplier => supplier._id === action.payload.id,
      )
      if (supplier) supplier.searchReindexResponse = action.payload.response
    },
    resetSearchReindex: (state, action) => {
      const supplier = state.suppliers.find(
        supplier => supplier._id === action.payload.id,
      )
      if (supplier) {
        supplier.loadingSearchReindex = false
        supplier.searchReindexResponse = null
      }
    },
    setPriceFiles: (state, action) => {
      state.priceFiles = action.payload
    },
    setPriceFileMetadata: (state, action) => {
      state.priceFileMetadata = {
        ...state.priceFileMetadata,
        [action.payload.supplierId]: action.payload.metadata,
      }
    },
    setPriceFilesLoading: (state, action) => {
      state.isPriceFilesLoading = action.payload
    },
    setMetadataLoading: (state, action) => {
      state.isMetadataLoading = action.payload
    },
    setOMSSuppliers: (state, action) => {
      state.omsSuppliers = action.payload
    },
  },
})

export const {
  setSuppliers,
  setPaginatedSuppliers,
  setSupplierStats,
  setSupplierLoading,
  addSupplierToState,
  deleteSupplierFromState,
  editSupplierInState,
  setSearchReindexLoading,
  setSearchReindexResponse,
  resetSearchReindex,
  setPriceFiles,
  setPriceFileMetadata,
  setPriceFilesLoading,
  setMetadataLoading,
  setOMSSuppliers,
} = supplierSlice.actions

export const fetchAllSuppliers = () => async dispatch => {
  try {
    const session = await Auth.currentAuthenticatedUser()
    const _suppliers = await API.get('api', 'suppliers', {
      queryStringParameters: {
        username: session.attributes.sub,
      },
    })
    dispatch(setSuppliers(_suppliers))
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}
export const downloadSupplierVenuesCSV = supplierId => async () => {
  try {
    const suppliers = await API.get('api', `suppliers/${supplierId}`)
    console.log('downloadSupplierVenuesCSV', suppliers)
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const addSupplier = supplier => async (dispatch, getState) => {
  const account = getState().auth?.account
  const prepared = applySupplierCreateBrandFlags(supplier, account)

  if (!prepared.ok) {
    return { success: false, error: prepared.error }
  }

  try {
    const _supplier = await API.post('api', `supplier`, {
      body: prepared.payload,
    })

    const refetchResult = await dispatch(fetchAllSuppliers())
    if (refetchResult === false) {
      dispatch(addSupplierToState(_supplier))
    }

    return {
      success: true,
      supplier: _supplier,
      displayTag: prepared.displayTag,
    }
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    const apiMessage =
      e?.response?.data?.message ||
      (typeof e?.response?.data === 'string' ? e.response.data : null)

    return {
      success: false,
      error: apiMessage || e?.message || 'Failed to create supplier',
    }
  }
}

export const editSupplier = supplier => async dispatch => {
  try {
    const _supplier = await API.post('api', `supplier`, {
      body: supplier,
    })
    dispatch(editSupplierInState(_supplier))
    return _supplier
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const deleteSupplier = supplier => async dispatch => {
  try {
    await API.del('api', `supplier/${supplier._id}`)
    dispatch(deleteSupplierFromState(supplier._id))
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const rebuildSupplierSearchIndex = supplier => async dispatch => {
  try {
    dispatch(setSearchReindexLoading({ id: supplier._id, loading: true }))
    let notifyEmail
    try {
      const session = await Auth.currentAuthenticatedUser()
      notifyEmail = session && session.attributes && session.attributes.email
    } catch (authError) {
      console.error(authError)
    }
    await API.get(
      'api',
      `admin/search/indexProducts/${supplier._id}`,
      notifyEmail ? { queryStringParameters: { notifyEmail } } : undefined,
    )
    dispatch(resetSearchReindex({ id: supplier._id }))
    SuccessNotification({
      message: 'Rebuild is in progress',
      description: notifyEmail
        ? `We'll notify you through email sent to ${notifyEmail}`
        : "We'll notify you when it's done.",
      duration: 6,
    })
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    dispatch(setSearchReindexLoading({ id: supplier._id, loading: false }))
    dispatch(
      setSearchReindexResponse({
        id: supplier._id,
        response: { success: false },
      }),
    )
    RejectedNotification({
      message: `Failed to start rebuild for ${
        supplier.title || 'this supplier'
      }`,
    })
    return false
  }
}

export const fetchPriceFiles = () => async dispatch => {
  try {
    dispatch(setPriceFilesLoading(true))
    const session = await Auth.currentAuthenticatedUser()
    const priceFiles = await API.get('api', 'supplier/price-files', {
      queryStringParameters: {
        username: session.attributes.sub,
      },
    })
    dispatch(setPriceFiles(priceFiles))
    dispatch(setPriceFilesLoading(false))
    return priceFiles
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    dispatch(setPriceFilesLoading(false))
    return false
  }
}

export const fetchPriceFileMetadata = supplierId => async dispatch => {
  try {
    dispatch(setMetadataLoading(true))
    const session = await Auth.currentAuthenticatedUser()
    const metadata = await API.get(
      'api',
      `supplier/${supplierId}/price-file-metadata`,
      {
        queryStringParameters: {
          username: session.attributes.sub,
        },
      },
    )
    dispatch(
      setPriceFileMetadata({
        supplierId,
        metadata,
      }),
    )
    dispatch(setMetadataLoading(false))
    return metadata
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    dispatch(setMetadataLoading(false))
    return false
  }
}

export const triggerIronWebPriceFileMappingExport = () => async () => {
  try {
    const session = await Auth.currentAuthenticatedUser()
    const response = await API.post(
      'api2',
      'suppliers/ironweb/price-file-mapping/export',
      {
        body: {
          username: session?.attributes?.sub,
        },
      },
    )

    return response
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const fetchOMSSuppliers = () => async dispatch => {
  try {
    const response = await API.get('api2', 'suppliers')
    // API returns { success: true, data: [...], message: "..." }
    const suppliers = response?.data || response || []
    dispatch(setOMSSuppliers(suppliers))
    return { success: true, data: suppliers }
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return { success: false, message: e?.message }
  }
}

export const quickAddSupplier =
  (data, options = {}) =>
  async () => {
    const {
      successMessage = 'Supplier added successfully.',
      skipSuccessModal = false,
    } = options
    try {
      const response = await API.post('api2', 'suppliers/quickAdd', {
        body: data,
      })

      if (response.success) {
        if (data?.venueId) {
          emitGrowthEngineGuidedLaneProgress(
            data.venueId,
            GROWTH_ENGINE_GUIDED_LANE_REASONS.SUPPLIER_ADDED,
          )
        }
        return { success: true, response, successMessage, skipSuccessModal }
      } else {
        return { success: false, message: response.message }
      }
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      return { success: false, message: e?.message }
    }
  }

export const requestNewSupplier =
  (
    venueId,
    supplierName,
    supplierEmail,
    accountNumber,
    note,
    sendEmail = false,
    options = {},
  ) =>
  async () => {
    const {
      manualSetup = false,
      alertMessage = "OMS will onboard this supplier soon. We'll be in touch if there are any issues, otherwise expect to see them added to your account in a few days.",
      toastMessage,
    } = options

    const body = {
      venueId,
      supplierName,
      supplierEmail,
      accountNumber: accountNumber || '',
      note: note || '',
      sendEmail,
      manualSetup,
    }

    try {
      const response = await API.post('api2', 'suppliers/add', {
        body,
      })

      if (response.success) {
        return {
          success: true,
          message: toastMessage || alertMessage,
        }
      } else {
        return { success: false, message: response.message }
      }
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      return { success: false, message: e?.message }
    }
  }

export default supplierSlice
